import {
  ErrorModal,
  MesfModal,
  TimeService,
  useShiftNavigator,
} from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid, TextField } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { useEffect, useState } from "react";
import {
  Controller,
  type SubmitHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { CommentsField } from "@/shared/fields/CommentsField";
import {
  useJustifyStrandLossMutation,
  useUpsertStrandLossMutation,
} from "../../hooks/mutations/useStrandLossesManagerMutations";
import { useStrandLossByIdQuery } from "../../hooks/queries/useStrandLossByIdQuery";
import type { StrandLossModal } from "../../hooks/useStrandLossesOptionFunctions";
import { STRAND_LOSS_MODAL } from "../../hooks/useStrandLossesOptionFunctions";
import type { StrandAsset } from "../../models/StrandAsset";
import type { StrandLossByShift } from "../../models/StrandLossByShift";
import { StrandAssetAutocomplete } from "./StrandAssetAutocomplete";
import { StrandLossCategoryAutocomplete } from "./StrandLossCategoryAutocomplete";
import { StrandLossCodeTreePicker } from "./StrandLossCodeTreePicker";

interface UpsertJustifyStrandLossProps {
  showModal: StrandLossModal;
  onHide: () => void;
  onSuccess?: () => Promise<void> | void;
  strandLossId: number | null;
  parentAssetId: number | null;
  strandAssets: StrandAsset[];
}

const DEFAULT_VALUES: StrandLossByShift = {
  StrandLossId: null,
  AssetId: null,
  AssetName: null,
  StrandLossCodeId: null,
  StrandLossCodeName: null,
  Start: null,
  End: null,
  Duration: 0,
  ShiftId: 0,
  Comments: "",
  JustifiedByUser: "",
  JustifiedDateTime: null,
  StrandLossCategoryId: null,
  IsManual: false,
  StrandLossCategoryName: "",
  StrandLossCategoryColor: "",
  ProductionDate: null,
  ProductionShift: 0,
  Shift: "",
  Crew: "",
  Heats: [],
  Strand: null,
};

const STRAND_LOSS_MODAL_TITLES: Record<StrandLossModal, string> = {
  [STRAND_LOSS_MODAL.NEW]: "New Strand Loss",
  [STRAND_LOSS_MODAL.EDIT]: "Edit Strand Loss",
  [STRAND_LOSS_MODAL.DELETE]: "Delete Strand Loss",
  [STRAND_LOSS_MODAL.JUSTIFY]: "Justify Strand Loss",
  [STRAND_LOSS_MODAL.CLOSED]: "",
};

export const UpsertJustifyStrandLoss = ({
  showModal,
  onHide,
  onSuccess,
  strandLossId,
  parentAssetId,
  strandAssets,
}: UpsertJustifyStrandLossProps) => {
  const [error, setError] = useState<string | null>(null);
  const [notificationMessage, setNotificationMessage] = useState("");
  const { shiftInfo } = useShiftNavigator();
  const upsertMutation = useUpsertStrandLossMutation();
  const justifyMutation = useJustifyStrandLossMutation();
  const isModalOpen =
    showModal !== STRAND_LOSS_MODAL.CLOSED &&
    showModal !== STRAND_LOSS_MODAL.DELETE;
  const { data: strandLossData, isLoading } = useStrandLossByIdQuery(
    strandLossId,
    isModalOpen && showModal !== STRAND_LOSS_MODAL.NEW,
  );
  const { setValue, control, handleSubmit, reset, getValues } =
    useForm<StrandLossByShift>({
      defaultValues: DEFAULT_VALUES,
    });
  const [start, end] = useWatch({ control, name: ["Start", "End"] });
  const title = STRAND_LOSS_MODAL_TITLES[showModal];

  useEffect(() => {
    if (!isModalOpen) return;

    if (strandLossId && strandLossData) {
      reset({
        ...DEFAULT_VALUES,
        StrandLossId: strandLossData.StrandLossId,
        AssetId: strandLossData.AssetId,
        StrandLossCodeId: strandLossData.StrandLossCodeId,
        Start: strandLossData.Start ?? shiftInfo?.CurrentStart ?? null,
        End: strandLossData.End,
        StrandLossCategoryId: strandLossData.StrandLossCategoryId,
        IsManual: strandLossData.IsManual,
        Comments: strandLossData.Comments,
      });
      return;
    }

    if (showModal === STRAND_LOSS_MODAL.NEW) {
      const defaultStart = shiftInfo?.CurrentStart
        ? new Date(shiftInfo.CurrentStart)
        : null;
      const defaultEnd = defaultStart ? new Date(defaultStart) : null;
      defaultEnd?.setHours(defaultEnd.getHours() + 1);
      reset({
        ...DEFAULT_VALUES,
        StrandLossId: 0,
        Start: defaultStart,
        End: defaultEnd,
        IsManual: true,
      });
    }
  }, [isModalOpen, showModal, strandLossId, strandLossData, reset, shiftInfo]);

  const duration = (() => {
    if (!start) return "";
    const durationValue = moment.duration(
      moment(end ?? new Date()).diff(moment(start)),
    );
    const suffix = end ? "" : " (Ongoing)";
    return `${Math.floor(durationValue.asMinutes())}m ${durationValue.seconds()}s${suffix}`;
  })();

  const finishSuccessfulMutation = async (message: string) => {
    setNotificationMessage(message);
    try {
      await onSuccess?.();
    } finally {
      onHide();
    }
  };

  const onSubmit: SubmitHandler<StrandLossByShift> = (data) => {
    if (showModal === STRAND_LOSS_MODAL.JUSTIFY) {
      justifyMutation.mutate(
        {
          strandLossId: data.StrandLossId!,
          strandLossCodeId: data.StrandLossCodeId,
          strandLossCategoryId: data.StrandLossCategoryId,
          comments: data.Comments || null,
        },
        {
          onSuccess: () =>
            finishSuccessfulMutation("Strand Loss was justified successfully"),
          onError: (mutationError) =>
            setError(
              mutationError instanceof Error
                ? mutationError.message
                : "Failed to justify Strand Loss",
            ),
        },
      );
      return;
    }

    upsertMutation.mutate(
      {
        strandLossId: data.StrandLossId === 0 ? null : data.StrandLossId,
        assetId: data.AssetId!,
        start: data.Start,
        end: data.End,
        strandLossCodeId: data.StrandLossCodeId,
        strandLossCategoryId: data.StrandLossCategoryId,
        comments: data.Comments || null,
      },
      {
        onSuccess: () => {
          const action =
            showModal === STRAND_LOSS_MODAL.NEW ? "created" : "edited";
          return finishSuccessfulMutation(
            `Strand Loss was ${action} successfully`,
          );
        },
        onError: (mutationError) =>
          setError(
            mutationError instanceof Error
              ? mutationError.message
              : "Failed to save Strand Loss",
          ),
      },
    );
  };

  const isSubmitLoading = upsertMutation.isPending || justifyMutation.isPending;
  const isDataLoading =
    isLoading && !!strandLossId && showModal !== STRAND_LOSS_MODAL.NEW;

  return (
    <>
      <MesfModal
        maxWidth="md"
        open={isModalOpen}
        handleClose={onHide}
        id="upsertStrandLoss"
        title={title}
      >
        {isDataLoading ? (
          <CenteredLazyLoading />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <MesfModal.Content dividers sx={{ padding: "15px 30px" }}>
              <Grid container spacing={2}>
                <Grid item xs={4} md={4}>
                  <Controller
                    name="Start"
                    control={control}
                    rules={{
                      deps: ["End"],
                      required: "Strand Loss Start is required",
                      validate: {
                        notAfterEnd: (value) => {
                          const endDate = getValues("End");
                          return !value || !endDate || value <= endDate
                            ? true
                            : "Strand Loss Start cannot be after End.";
                        },
                        notInFuture: (value) =>
                          !value || value <= new Date()
                            ? true
                            : "Strand Loss Start cannot be in the future.",
                      },
                    }}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <DateTimePicker
                        disabled={showModal === STRAND_LOSS_MODAL.JUSTIFY}
                        label="Strand Loss Start"
                        timezone={TimeService.getInstance().getServerTimeZone()}
                        format="MM/DD/YYYY HH:mm:ss"
                        ampm={false}
                        value={field.value ? moment(field.value) : null}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            error: !!fieldError,
                            helperText: fieldError?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={4}>
                  <Controller
                    name="End"
                    control={control}
                    rules={{
                      deps: ["Start"],
                      validate: {
                        notBeforeStart: (value) => {
                          const startDate = getValues("Start");
                          return !value || !startDate || value >= startDate
                            ? true
                            : "Strand Loss End cannot be before Start.";
                        },
                        notInFuture: (value) =>
                          !value || value <= new Date()
                            ? true
                            : "Strand Loss End cannot be in the future.",
                        notSameAsStart: (value) => {
                          const startDate = getValues("Start");
                          return !value ||
                            !startDate ||
                            !moment(value).isSame(moment(startDate))
                            ? true
                            : "Strand Loss End cannot be the same as Start.";
                        },
                      },
                    }}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <DateTimePicker
                        disabled={showModal === STRAND_LOSS_MODAL.JUSTIFY}
                        label="Strand Loss End"
                        timezone={TimeService.getInstance().getServerTimeZone()}
                        format="MM/DD/YYYY HH:mm:ss"
                        ampm={false}
                        value={field.value ? moment(field.value) : null}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            error: !!fieldError,
                            helperText: fieldError?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={4}>
                  <TextField
                    label="Duration (min)"
                    disabled
                    variant="outlined"
                    fullWidth
                    value={duration}
                    autoComplete="off"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={4} md={4}>
                  <Controller
                    name="AssetId"
                    control={control}
                    rules={{ required: "Strand is required" }}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <StrandAssetAutocomplete
                        assets={strandAssets}
                        value={field.value}
                        onChange={field.onChange}
                        fieldError={fieldError}
                        disabled={showModal === STRAND_LOSS_MODAL.JUSTIFY}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={4}>
                  <Controller
                    name="StrandLossCodeId"
                    control={control}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <StrandLossCodeTreePicker
                        value={field.value}
                        onChange={(value, defaultCategoryId) => {
                          field.onChange(value);
                          setValue("StrandLossCategoryId", defaultCategoryId);
                        }}
                        parentAssetId={parentAssetId}
                        fieldError={fieldError}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={4} md={4}>
                  <Controller
                    name="StrandLossCategoryId"
                    control={control}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <StrandLossCategoryAutocomplete
                        value={field.value}
                        onChange={field.onChange}
                        parentAssetId={parentAssetId}
                        fieldError={fieldError}
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item md={12} xs={12}>
                  <Controller
                    name="Comments"
                    control={control}
                    render={({ field, fieldState: { error: fieldError } }) => (
                      <CommentsField
                        value={field.value}
                        onChange={field.onChange}
                        fieldError={fieldError}
                        getValues={getValues}
                        rows={{ minRows: 3, maxRows: 3 }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </MesfModal.Content>
            <MesfModal.Actions sx={{ padding: "20px 30px 30px" }}>
              <Grid container spacing={2} justifyContent="flex-end">
                <Grid item md={3} xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="inherit"
                    onClick={onHide}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item md={3} xs={12}>
                  <Button
                    fullWidth
                    startIcon={
                      isSubmitLoading ? <CircularProgress size="1rem" /> : null
                    }
                    disabled={isSubmitLoading}
                    variant="contained"
                    color="primary"
                    type="submit"
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </MesfModal.Actions>
          </form>
        )}
      </MesfModal>
      <ErrorModal error={error ?? ""} onHide={() => setError(null)} />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
    </>
  );
};
